import React, { ChangeEvent, useState } from "react";
import { Button } from "@/components/Button";
import DrawerHeader from "@/components/DrawerHeader";
import FullScreenDrawer from "@/components/FullScreenDrawer";
import { useAccountpageContext } from "../AccountPageContext";
import { ModifiedCharge } from "../RechargeApi";
import { DrawerItemCard } from "../DrawerItemCard";
import { variantSelect, getSizeInfo } from "@/store";
import { Money } from "@/helpers/Money";
import { parseDeliveryFrequency } from "../helper";
import { getDisplayDate } from "../../portal/helpers";
import { DatePicker } from "@/components/DatePicker";
import { getIsoDateFormat } from "@/helpers/utils";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { NotificationManagement } from "../../notification/NotificationManagement";
import { NotificationProps } from "../../notification/Notification";
import { createSubscriptionItem } from "../../portal/RechargeApiActions";
import { track } from "@/helpers/googleAnalytics";
import { RECHARGE_BASE_URL } from "../RechargeApi";
import { useRechargeCustomer } from "../RechargeCustomerContext";

export const SubscriptionDrawer = ({
  open,
  onExit,
  addressId,
  upcomingCharge,
}: {
  open: boolean;
  onExit: () => void;
  addressId: number | null;
  upcomingCharge: ModifiedCharge | null;
}) => {
  const { token: customerToken, hash: customerHash } =
    useRechargeCustomer() || {};
  const { selectedLineItem } = useAccountpageContext() || {};
  const currDate = upcomingCharge?.scheduleAt;

  const [newDate, setNewDate] = useState(currDate ?? new Date());
  const [pageStep, setPageStep] = useState<1 | 2>(1);
  const [currNotification, setCurrNotification] =
    useState<null | NotificationProps>(null);
  const [loading, setLoading] = useState(false);

  if (!addressId) return null;

  if (
    selectedLineItem?.product?.selling_plan_groups === undefined ||
    selectedLineItem?.product?.selling_plan_groups.length === 0 ||
    selectedLineItem === undefined
  ) {
    return null;
  }

  if (
    !selectedLineItem.variant ||
    !selectedLineItem.variant.available ||
    !selectedLineItem.variantId
  ) {
    return null;
  }

  const variantObject = {
    ...selectedLineItem.variant,
    size: getSizeInfo(selectedLineItem.variant.options),
  };

  const planName = "Delivery every 4 Months";
  const sellingPlan =
    selectedLineItem?.product?.selling_plan_groups[0]?.selling_plans?.find(
      (plan) => plan.name === planName
    );

  if (!sellingPlan) {
    return <div>Subscription is not available for this product</div>;
  }

  const discountValue =
    sellingPlan?.price_adjustments && sellingPlan.price_adjustments.length > 0
      ? sellingPlan.price_adjustments[0].value
      : null;
  const variantAlloc = selectedLineItem.variant.selling_plan_allocations[0];

  const priceCents = variantAlloc.price * (selectedLineItem?.quantity ?? 1);
  const compareAtPriceCents =
    variantAlloc.compare_at_price * (selectedLineItem?.quantity ?? 1);
  const compareAtPriceMoney =
    compareAtPriceCents !== priceCents
      ? Money.from(compareAtPriceCents, "¢")
      : undefined;
  const priceMoney = Money.from(priceCents, "¢");

  const shippingInfo = parseDeliveryFrequency(sellingPlan.name);

  const productCardValue = {
    quantity: selectedLineItem?.quantity,
    product: {
      title: selectedLineItem.productTitle,
      featuredImage: {
        ...selectedLineItem.image,
      },
      size: variantSelect.sizeFormat(variantObject),
      compareAtPrice: compareAtPriceMoney,
    },
    price: priceMoney,
    shippingInfo,
  };

  const handleDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(event.target.value + "T00:00:00");
    if (!isNaN(+newDate)) {
      setNewDate(newDate);
    }
  };
  const handleButtonClick = async () => {
    if (!selectedLineItem.variantId) {
      return null;
    }
    setLoading(true);
    const properties = {
      "properties[_source-module]": "account-page-save-with-sub-btn",
    };
    const request = {
      shopifyVariantId: selectedLineItem.variantId,
      quantity: selectedLineItem.quantity,
      addressId: addressId,
      cadence: shippingInfo,
      nextChargeDate: newDate,
      properties,
    };

    try {
      const requestUrl = `${RECHARGE_BASE_URL}${customerHash}/subscriptions?token=${customerToken}`;
      const subscription = await createSubscriptionItem(request, requestUrl);
      if (subscription) {
        track({
          name: "Portal Add Item",
          item: subscription,
          sourceModule: properties["properties[_source-module]"],
        });
        setLoading(false);
        setPageStep(2);
        setCurrNotification({
          title: "Created Subscription",
          message: `Your subscription will be processed on ${getDisplayDate(
            newDate
          )}.`,
        });
      } else {
        setLoading(false);
        setCurrNotification({
          title: "Something went wrong",
          message: "Failed to create your subscription",
          severity: "error",
        });
      }
    } catch (error) {
      setLoading(false);
      setCurrNotification({
        title: "Something went wrong",
        message: "Failed to create your subscription" + error,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <FullScreenDrawer
        anchor="right"
        open={open}
        fullscreen={undefined}
        role="dialog"
        aria-label="formulation"
        aria-modal={true}
        onClose={() => {
          onExit();
          setPageStep(1);
        }}
      >
        <DrawerHeader
          sticky={true}
          handleClose={() => {
            onExit();
            setPageStep(1);
          }}
          text="create subscription"
        />
        {pageStep === 1 && (
          <div className="w-full space-y-8 p-5">
            <div>
              <p className="subtitle mb-4 text-base font-semibold uppercase">
                subscription review
              </p>
              <DrawerItemCard
                {...productCardValue}
                scentBg="white"
                className="rounded border border-solid border-primary"
              />
            </div>
            <div>
              <p className="subtitle mb-2 text-base font-semibold uppercase">
                CONFIRM SHIP DATE
              </p>
              {upcomingCharge && upcomingCharge.scheduleAt && (
                <p className="mb-4 text-sm">
                  You are subscribed to another Dropps product which ships on{" "}
                  {getDisplayDate(upcomingCharge.scheduleAt)}.{" "}
                  <span className="font-bold">
                    Orders over $$35 get FREE SHIPPING
                  </span>
                  , so you could save money by shipping on the date below.{" "}
                </p>
              )}

              <div className="mb-4">
                <DatePicker
                  name="next-shipment-date"
                  value={getIsoDateFormat(newDate)}
                  onChange={handleDateChange}
                  min={getIsoDateFormat(new Date())}
                  className="appearance-none"
                />
              </div>
              <p className="text-sm">
                {`Once you're happy with the ship date above, click `}
                <span className="font-bold">CONFIRM</span>
                {discountValue
                  ? ` to lock in your ${discountValue}% discount.`
                  : "."}
              </p>
            </div>
            <div className="space-y-4">
              <Button
                data-account-button="create-subscription"
                className="w-full"
                size="lg"
                loading={loading}
                onClick={handleButtonClick}
              >
                Confirm subscription{" "}
              </Button>
              <div className="flex space-x-2">
                <SettingsOutlinedIcon className="text-base" />
                <p className="text-sm">Change, skip, or cancel at anytime.</p>
              </div>
            </div>
          </div>
        )}
        {pageStep === 2 && (
          <div className="w-full space-y-8 p-5">
            <div className="space-y-4">
              <p className="subtitle text-base font-semibold uppercase">
                SUBSCRIPTION CREATED!
              </p>
              <p className="text-sm">
                No order was processed at this time. Your payment method will be
                charged when the product ships.{" "}
                <span className="font-bold">
                  See the subscriptions page for full details about your
                  upcoming order.
                </span>{" "}
              </p>
              <DrawerItemCard
                {...productCardValue}
                scentBg="white"
                className="rounded border border-solid border-primary"
              />
            </div>
            <div className="overflow-hidden rounded-lg border border-solid border-primary p-4">
              <p className="text-sm">Your Next Box Ships On:</p>
              <p className="body text-3xl font-semibold text-primary xl:text-2xl">
                {getDisplayDate(newDate)}
              </p>
            </div>
            <div className="space-y-4">
              <Button
                className="w-full "
                size="lg"
                onClick={() => {
                  onExit();
                  setPageStep(1);
                }}
              >
                Close
              </Button>
              <Button
                className="w-full "
                size="lg"
                variant="outline"
                component="a"
                href={`/tools/recurring/portal/${customerHash}/subscriptions?token=${customerToken}`}
              >
                view ALL SUBSCRIPTIONS
              </Button>
            </div>
          </div>
        )}
      </FullScreenDrawer>
      <NotificationManagement notification={currNotification} />
    </>
  );
};
